import { useMemo, useState } from "react";
import { Search, Check, ImageOff, X, Layers } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  decodeOutfit,
  encodeOutfit,
  hasAddon,
  pickOutfitImage,
  outfitGenderSymbol,
  outfitAddonBadge,
  type OutfitImages,
} from "@/lib/outfit-selection";
import { SpriteSheet, isSpriteSheet } from "@/components/personagem/SpriteSheet";

export const AddonBadge = ({ addons }: { addons: string }) => {
  const label = outfitAddonBadge(addons);
  if (!label) return null;
  return (
    <span className="absolute top-1 left-1/2 -translate-x-1/2 z-10 px-1.5 py-0.5 rounded-sm bg-primary text-primary-foreground text-[8px] font-bold leading-none whitespace-nowrap shadow">
      {label}
    </span>
  );
};

type GenderFilter = "all" | "M" | "F";

export interface OutfitFull extends OutfitImages {
  nome: string;
  genero: string;
  tipo?: string | null;
}

interface OutfitPickerProps {
  options: OutfitFull[];
  selected: string[]; // encoded entries
  onChange: (values: string[]) => void;
}

const keyOf = (nome: string, genero: string) => `${nome}__${genero}`;

const GenderBadge = ({ genero }: { genero: string }) => {
  const sym = outfitGenderSymbol(genero);
  if (!sym) return null;
  return (
    <span
      className={`absolute top-1 left-1 z-10 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold leading-none ${
        sym === "M" ? "bg-blue-500/90 text-white" : "bg-pink-500/90 text-white"
      }`}
      title={sym === "M" ? "Masculino" : "Feminino"}
    >
      {sym === "M" ? "♂" : "♀"}
    </span>
  );
};

const OutfitImage = ({ src, disabled }: { src?: string | null; disabled?: boolean }) => {
  if (!src) {
    return (
      <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
        <ImageOff className="w-5 h-5" />
      </div>
    );
  }
  // RubinOT custom outfits are animated sprite strips, not single images.
  if (isSpriteSheet(src)) {
    return <SpriteSheet src={src} disabled={disabled} />;
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className={`w-full h-full object-contain scale-125 ${disabled ? "grayscale opacity-50" : ""}`}
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
    />
  );
};

const OutfitPicker = ({ options, selected, onChange }: OutfitPickerProps) => {
  const [search, setSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState<GenderFilter>("all");
  const [view, setView] = useState<"geral" | "selecionados">("geral");
  const [editing, setEditing] = useState<OutfitFull | null>(null);
  const [draftAddons, setDraftAddons] = useState<string>("");
  const [hoverFull, setHoverFull] = useState(false);

  // Map encoded-selection -> { addons } and quick lookup by nome+genero
  const selectedByKey = useMemo(() => {
    const m = new Map<string, string>(); // key -> addons
    for (const raw of selected) {
      const { nome, genero, addons } = decodeOutfit(raw);
      m.set(keyOf(nome, genero), addons);
    }
    return m;
  }, [selected]);

  const optByKey = useMemo(() => {
    const m = new Map<string, OutfitFull>();
    for (const o of options) m.set(keyOf(o.nome, o.genero), o);
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return options.filter(
      (o) =>
        (!q || o.nome.toLowerCase().includes(q)) &&
        (genderFilter === "all" || outfitGenderSymbol(o.genero) === genderFilter),
    );
  }, [options, search, genderFilter]);

  const selectedList = useMemo(
    () =>
      selected
        .map((raw) => {
          const { nome, genero, addons } = decodeOutfit(raw);
          const opt = optByKey.get(keyOf(nome, genero));
          return { raw, nome, genero, addons, opt };
        })
        .filter((s) => !!s.opt)
        .filter(
          (s) => genderFilter === "all" || outfitGenderSymbol(s.genero) === genderFilter,
        ) as {
        raw: string; nome: string; genero: string; addons: string; opt: OutfitFull;
      }[],
    [selected, optByKey, genderFilter],
  );


  const openEditor = (opt: OutfitFull) => {
    setDraftAddons(selectedByKey.get(keyOf(opt.nome, opt.genero)) ?? "");
    setEditing(opt);
  };

  const removeSelection = (opt: OutfitFull) => {
    const k = keyOf(opt.nome, opt.genero);
    onChange(
      selected.filter((raw) => {
        const { nome, genero } = decodeOutfit(raw);
        return keyOf(nome, genero) !== k;
      }),
    );
  };

  const confirmEditor = () => {
    if (!editing) return;
    const k = keyOf(editing.nome, editing.genero);
    const rest = selected.filter((raw) => {
      const { nome, genero } = decodeOutfit(raw);
      return keyOf(nome, genero) !== k;
    });
    onChange([
      ...rest,
      encodeOutfit({ nome: editing.nome, genero: editing.genero, addons: draftAddons }),
    ]);
    setEditing(null);
  };

  const d1 = hasAddon(draftAddons, 1);
  const d2 = hasAddon(draftAddons, 2);
  const fullEnabled = d1 && d2;

  return (
    <div className="h-full min-h-0 flex flex-col gap-2 overflow-hidden">
      <div className="tibia-input rounded-sm flex items-center gap-2 shrink-0">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar outfit..."
          className="bg-transparent text-sm w-full outline-none min-w-0"
        />
      </div>

      <div className="flex items-center gap-1 tibia-border-inner bg-secondary/20 rounded-sm p-1 shrink-0">
        {([
          { v: "all", label: "Ambos" },
          { v: "M", label: "♂ Masculino" },
          { v: "F", label: "♀ Feminino" },
        ] as { v: GenderFilter; label: string }[]).map((g) => (
          <button
            key={g.v}
            type="button"
            onClick={() => setGenderFilter(g.v)}
            className={`flex-1 rounded-sm px-2 py-1 text-xs font-medium transition-colors ${genderFilter === g.v ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
          >
            {g.label}
          </button>
        ))}
      </div>


      <div className="flex items-center gap-1 tibia-border-inner bg-secondary/20 rounded-sm p-1 shrink-0">
        <button
          type="button"
          onClick={() => setView("geral")}
          className={`flex-1 rounded-sm px-3 py-1 text-xs font-medium transition-colors ${view === "geral" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
        >
          Geral ({options.length})
        </button>
        <button
          type="button"
          onClick={() => setView("selecionados")}
          className={`flex-1 rounded-sm px-3 py-1 text-xs font-medium transition-colors ${view === "selecionados" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
        >
          Selecionados ({selected.length})
        </button>
      </div>

      {view === "geral" ? (
        <div className="flex-1 min-h-0 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 auto-rows-max gap-2 overflow-y-auto p-0.5">
          {filtered.length === 0 && (
            <div className="col-span-full p-4 text-center text-xs text-muted-foreground">Nenhum resultado</div>
          )}
          {filtered.map((o) => {
            const k = keyOf(o.nome, o.genero);
            const isSel = selectedByKey.has(k);
            const addons = selectedByKey.get(k) ?? "";
            const img = isSel ? pickOutfitImage(o, addons) : o.img_outfit ?? null;
            return (
              <div
                key={k}
                onClick={() => openEditor(o)}
                className={`relative tibia-border-inner rounded-sm bg-secondary/20 p-1.5 pt-6 flex flex-col items-center gap-1 text-center cursor-pointer ${isSel ? "ring-2 ring-primary bg-primary/10" : ""}`}
              >
                <GenderBadge genero={o.genero} />
                {isSel && <Check className="absolute top-1 right-1 w-3.5 h-3.5 text-primary z-10" />}
                {isSel && <AddonBadge addons={addons} />}
                <div className="relative">
                  <span
                    className="w-10 h-10 flex items-center justify-center overflow-hidden"
                    title="Selecionar add-ons"
                  >
                    <OutfitImage src={img} />
                  </span>
                </div>
                <span className="text-[10px] leading-tight line-clamp-2 text-foreground">{o.nome}</span>
                <span className="text-[9px] flex items-center gap-0.5 text-primary">
                  <Layers className="w-2.5 h-2.5" /> addons
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 auto-rows-max gap-2 overflow-y-auto p-0.5">
          {selectedList.length === 0 && (
            <div className="col-span-full p-4 text-center text-xs text-muted-foreground">Nenhum outfit selecionado</div>
          )}
          {selectedList.map(({ raw, nome, genero, addons, opt }) => (
            <div
              key={raw}
              onClick={() => openEditor(opt)}
              className="relative tibia-border-inner rounded-sm bg-primary/10 ring-2 ring-primary p-1.5 pt-6 flex flex-col items-center gap-1 text-center cursor-pointer hover:brightness-110 transition-all"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(opt); } }}
            >
              <GenderBadge genero={genero} />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeSelection(opt); }}
                className="absolute top-1 right-1 z-10 text-muted-foreground hover:text-destructive"
                title="Remover"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <AddonBadge addons={addons} />
              <div className="relative">
                <span className="w-10 h-10 flex items-center justify-center overflow-hidden">
                  <OutfitImage src={pickOutfitImage(opt, addons)} />
                </span>
              </div>
              <span className="text-[10px] leading-tight line-clamp-2 text-foreground">{nome}</span>
            </div>
          ))}
        </div>
      )}

      {/* Add-on selection dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              {editing?.nome}
              {editing && <GenderBadgeInline genero={editing.genero} />}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <>
              <p className="text-xs text-muted-foreground">
                Selecione os add-ons que o personagem possui. Confirme sem selecionar nada para anunciar apenas a outfit padrão.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {/* Addon 1 */}
                <button
                  type="button"
                  onClick={() => setDraftAddons(toggle(draftAddons, 1))}
                  className={`relative tibia-border-inner rounded-sm bg-secondary/20 p-3 flex flex-col items-center gap-2 transition-all hover:brightness-110 ${d1 ? "ring-2 ring-primary bg-primary/10" : ""}`}
                >
                  {d1 && <Check className="absolute top-1.5 right-1.5 w-4 h-4 text-primary z-10" />}
                  <span className="w-24 h-24 flex items-center justify-center">
                    <OutfitImage src={editing.img_addon1} />
                  </span>
                  <span className="text-xs text-foreground">Addon 1</span>
                </button>
                {/* Addon 2 */}
                <button
                  type="button"
                  onClick={() => setDraftAddons(toggle(draftAddons, 2))}
                  className={`relative tibia-border-inner rounded-sm bg-secondary/20 p-3 flex flex-col items-center gap-2 transition-all hover:brightness-110 ${d2 ? "ring-2 ring-primary bg-primary/10" : ""}`}
                >
                  {d2 && <Check className="absolute top-1.5 right-1.5 w-4 h-4 text-primary z-10" />}
                  <span className="w-24 h-24 flex items-center justify-center">
                    <OutfitImage src={editing.img_addon2} />
                  </span>
                  <span className="text-xs text-foreground">Addon 2</span>
                </button>
                {/* Completo (not selectable) */}
                <div
                  onMouseEnter={() => setHoverFull(true)}
                  onMouseLeave={() => setHoverFull(false)}
                  className={`relative tibia-border-inner rounded-sm bg-secondary/20 p-3 flex flex-col items-center gap-2 transition-all hover:brightness-110 ${fullEnabled ? "ring-2 ring-primary bg-primary/10 shadow-[0_0_15px_hsl(var(--primary)/0.3)]" : ""}`}
                  title={fullEnabled ? "Outfit completo" : "Selecione os 2 add-ons"}
                >
                  {fullEnabled && <Check className="absolute top-1.5 right-1.5 w-4 h-4 text-primary z-10" />}
                  <span className="w-24 h-24 flex items-center justify-center">
                    <OutfitImage src={editing.img_completo} disabled={!fullEnabled && !hoverFull} />
                  </span>
                  <span className={`text-xs ${fullEnabled ? "text-foreground font-medium" : "text-muted-foreground"}`}>Completo</span>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                {selectedByKey.has(keyOf(editing.nome, editing.genero)) && (
                  <button
                    type="button"
                    onClick={() => { removeSelection(editing); setEditing(null); }}
                    className="tibia-tab rounded-sm px-4 py-2 text-sm flex items-center gap-1"
                  >
                    <X className="w-4 h-4" /> Remover
                  </button>
                )}
                <button
                  type="button"
                  onClick={confirmEditor}
                  className="tibia-btn rounded-sm flex-1 flex items-center justify-center gap-1 py-2"
                >
                  <Check className="w-4 h-4" /> Confirmar
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GenderBadgeInline = ({ genero }: { genero: string }) => {
  const sym = outfitGenderSymbol(genero);
  if (!sym) return null;
  return (
    <span className={`text-sm font-bold ${sym === "M" ? "text-blue-500" : "text-pink-500"}`}>
      {sym === "M" ? "♂" : "♀"}
    </span>
  );
};

// local re-export to keep JSX above tidy
const toggle = (addons: string, n: 1 | 2): string => {
  const a1 = hasAddon(addons, 1);
  const a2 = hasAddon(addons, 2);
  const next1 = n === 1 ? !a1 : a1;
  const next2 = n === 2 ? !a2 : a2;
  return `${next1 ? "1" : ""}${next2 ? "2" : ""}`;
};

export default OutfitPicker;
